import { GetStaticPaths, NextPage } from 'next';
import { useRouter } from 'next/router';

import { RestWorldRating, globalServices, jobPositionService } from '@restworld/data-services';
import { EntityId, JobPosition, Restaurant, Variable } from '@restworld/utility-types';

import { JobPositionSEO, JobPositionPage as JobPositionPageComponent, Loader } from '../../components';
import { getServerTranslations } from '../../lib/i18n/getServerTranslations';
import { minifyJobPosition } from '../../lib/job-position/minify-job-position';
import { isJobPositionMissing } from '../../lib/seo/job-position-not-found';
import { isJobPositionOnline } from '../../lib/seo/job-posting-fields';
import HirematicPixel from '../../components/job-position/analytics/hirematic-pixel';
import type { PosizionePageProps } from '../../components/job-position/job-position.types';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

export const JobPositionPage: NextPage<PosizionePageProps> = (props: PosizionePageProps) => {
  const router = useRouter();
  if (router.isFallback) return <Loader />;
  const jobTitle = props?.jobPositionStatic?.job_title || props?.jobPositionStatic?.occupation?.label_it?.label || '';
  return (
    <>
      {jobTitle && <h1 className="sr-only">{jobTitle}</h1>}
      <JobPositionPageComponent {...props} />
      {props?.jobPositionStatic ? <JobPositionSEO {...props} slug={props.slug as string} /> : null}
      {props?.jobPositionStatic?.hirematic_flag && <HirematicPixel />}
    </>
  );
};
export default JobPositionPage;

export const getStaticPaths: GetStaticPaths = async () => {
  // Skip pre-rendering during build — pages are generated on-demand via fallback: true
  // This avoids SSG errors with React 19's stricter rendering and speeds up builds.
  // (Master's SEO PR re-added build-time path generation; kept the migration's on-demand
  // approach for build stability — the JobPosting JSON-LD fixes live in getStaticProps below.)
  return { paths: [], fallback: true };
};

export const getStaticProps = async ({ params: { slug }, locale }) => {
  const transConfig = await getServerTranslations(locale, [
    'common',
    'job-position-page',
    'onboarding-page',
    'auth-flow',
    'job-explorer-page',
    'job-application-page',
  ]);
  const staticProps = {
    revalidate: 60,
    props: {
      jobPositionStatic: null,
      errorCode: null,
      restworldGoogleRatingData: null,
      slug,
      jobActiveStatus: null,
      domainInterest: null,
    },
  };
  if (!slug) return staticProps;

  // The job position itself decides whether this page exists, so it is loaded
  // on its own. A 404 from any of the auxiliary calls below must never be read
  // as "this listing does not exist".
  let jobPositionStatic: JobPosition;
  try {
    jobPositionStatic = (await jobPositionService({ baseUrl }).showJobPosition(slug as string)).data;
  } catch (error) {
    if (isJobPositionMissing(error)) {
      // The only form Next.js turns into an HTTP 404: a top-level key of the
      // result, not a prop. It used to be set as a prop, so the page answered
      // 200 with a "not found" body, which keeps the slug indexable.
      return { notFound: true, revalidate: 60 };
    }
    // Anything else (5xx, timeout, connection reset) is us, not the listing.
    // Throwing lets ISR keep serving the last good version of this page instead
    // of caching a soft 404 over it; on a page never generated before the
    // visitor gets a 500, which at least does not tell Google the ad is gone.
    console.error(`Error getting static job position data for ${slug}`, error);
    throw error;
  }

  // The active status decides whether the page shows the apply button or the
  // reopen flow, so a failure here cannot fall back to `null`: the page reads
  // `!jobActiveStatus` as offline and would hide candidatures on a live listing.
  // `status_key` comes from the job position we already have and says the same
  // thing.
  let jobActiveStatus: boolean;
  try {
    jobActiveStatus = (await jobPositionService({ baseUrl }).showJobPositionStatus(jobPositionStatic.id)).data;
  } catch (error) {
    console.error(`Error getting job position active status for ${slug}`, error);
    jobActiveStatus = isJobPositionOnline(jobPositionStatic?.status_key);
  }

  // These two only enrich the page. A failure degrades the page, it does not
  // remove it.
  let restworldGoogleRatingData: RestWorldRating | null = null;
  let domainInterest: Variable[] | null = null;
  try {
    restworldGoogleRatingData = (await globalServices({ baseUrl }).fetchRestworldRating()).data;
    domainInterest = (await globalServices({ baseUrl }).fetchVariableOptions('domain_interest')).data;
  } catch (error) {
    console.error(`Error getting auxiliary job position data for ${slug}`, error);
  }

  return {
    ...staticProps,
    props: {
      ...staticProps.props,
      ...transConfig,
      ...(jobPositionStatic ? { jobPositionStatic: minifyJobPosition(jobPositionStatic) } : {}),
      restworldGoogleRatingData,
      jobActiveStatus,
      slug,
      domainInterest,
    },
  };
};

type StaticProps = {
  params: {
    slug: string;
  };
};
// Re-export for backward compatibility — type is defined in components/job-position/job-position.types.ts
export type { PosizionePageProps } from '../../components/job-position/job-position.types';

type Identity<T> = { [P in keyof T]: T[P] };
type Replace<T, K extends keyof T, TReplace> = Identity<
  Pick<T, Exclude<keyof T, K>> & {
    [P in K]: TReplace;
  }
>;
